/**
 * Ambient market traces for the outer landing canvas.
 * One abstract sequence, rendered as candles on the left and a quote
 * trace on the right. The rails stay continuous; they never swap modes.
 */

'use client'

import React from 'react'

const GUIDE_LEVELS = [240, 440, 640, 840] as const
const VERTICAL_GUIDES = [60, 120, 180, 240] as const
const TILE_WIDTH = 320
const SCROLL_OFFSETS = [0, TILE_WIDTH] as const

type Candle = { id: string; open: number; close: number; high: number; low: number }

/* Chained OHLC: every bar opens at the previous close and the tile loops cleanly. */
const CANDLES: readonly Candle[] = [
  { id: 'c1', open: 520, close: 458, high: 438, low: 538 },
  { id: 'c2', open: 458, close: 486, high: 444, low: 510 },
  { id: 'c3', open: 486, close: 414, high: 394, low: 502 },
  { id: 'c4', open: 414, close: 446, high: 400, low: 472 },
  { id: 'c5', open: 446, close: 376, high: 352, low: 460 },
  { id: 'c6', open: 376, close: 422, high: 362, low: 448 },
  { id: 'c7', open: 422, close: 460, high: 408, low: 488 },
  { id: 'c8', open: 460, close: 398, high: 378, low: 474 },
  { id: 'c9', open: 398, close: 452, high: 384, low: 478 },
  { id: 'c10', open: 452, close: 520, high: 436, low: 546 },
]

/*
 * The quote rail follows the same close sequence as the candle rail.
 * Matching first and last values keeps the repeated tile seamless.
 */
const PRICE = [520, 492, 458, 472, 486, 450, 414, 432, 446, 410, 376, 398, 422, 442, 460, 428, 398, 424, 452, 486, 520]
const AVERAGE = [520, 506, 492, 480, 472, 458, 444, 436, 428, 418, 408, 404, 410, 420, 432, 438, 432, 436, 452, 484, 520]
const LINE_VOLUME = [42, 58, 74, 52, 88, 66, 104, 82, 116, 92, 128, 86, 72, 98, 112, 76, 64, 90, 108, 68] as const
const TERMINAL_SMA20 = [510, 492, 474, 452, 432, 416, 422, 438, 448, 472, 510]
const TERMINAL_SMA50 = [500, 492, 482, 468, 452, 440, 434, 438, 450, 474, 500]
const TERMINAL_STEP = [842, 824, 848, 810, 828, 786, 802, 770, 794, 758, 842]

function smoothLinePath(points: readonly number[], span = TILE_WIDTH) {
  const width = span / (points.length - 1)
  return points.reduce((path, y, index) => {
    const x = index * width
    if (index === 0) return `M${x} ${y}`

    const previousX = (index - 1) * width
    const previousY = points[index - 1]!
    const midpoint = (previousX + x) / 2
    return `${path} C${midpoint} ${previousY}, ${midpoint} ${y}, ${x} ${y}`
  }, '')
}

function stepPath(points: readonly number[]) {
  const width = TILE_WIDTH / (points.length - 1)
  return points
    .map((y, index) => (index === 0 ? `M0 ${y}` : `H${index * width} V${y}`))
    .join(' ')
}

type ChartFieldProps = {
  side: 'left' | 'right'
}

const ChartField: React.FC<ChartFieldProps> = ({ side }) => {
  const isLeft = side === 'left'
  const pricePath = smoothLinePath(PRICE)
  const averagePath = smoothLinePath(AVERAGE)
  const terminalSma20Path = smoothLinePath(TERMINAL_SMA20)
  const terminalSma50Path = smoothLinePath(TERMINAL_SMA50)

  return (
    <svg
      className={`landing-market-field landing-market-field-${side}`}
      viewBox="0 0 320 1000"
      preserveAspectRatio="none"
    >
      {GUIDE_LEVELS.map((y) => (
        <line key={y} className="landing-market-guide" x1="0" x2="320" y1={y} y2={y} />
      ))}
      {VERTICAL_GUIDES.map((x) => (
        <line key={x} className="landing-market-guide" x1={x} x2={x} y1="180" y2="900" />
      ))}
      <line className="landing-tv-price-axis" x1="304" x2="304" y1="180" y2="900" />
      <line className="landing-tv-time-axis" x1="0" x2="304" y1="900" y2="900" />
      {GUIDE_LEVELS.slice(0, 3).map((y) => (
        <line key={y} className="landing-tv-price-tick" x1="296" x2="310" y1={y} y2={y} />
      ))}
      {VERTICAL_GUIDES.map((x) => (
        <line key={x} className="landing-tv-time-tick" x1={x} x2={x} y1="894" y2="906" />
      ))}
      <line className="landing-tv-volume-divider" x1="0" x2="304" y1="700" y2="700" />
      <line className="landing-tv-last-price" x1="0" x2="304" y1={PRICE[0]} y2={PRICE[0]} />
      {isLeft ? (
        <g className="landing-terminal-levels">
          <line className="landing-terminal-level is-buy" x1="44" x2="304" y1="396" y2="396" />
          <line className="landing-terminal-level is-sell" x1="102" x2="304" y1="554" y2="554" />
        </g>
      ) : null}

      {isLeft ? (
        <>
          <g className="landing-market-scroll-track">
            {SCROLL_OFFSETS.map((offset) => (
              <g key={offset} transform={`translate(${offset} 0)`}>
                <path
                  className="landing-terminal-indicator landing-terminal-indicator-fast"
                  d={terminalSma20Path}
                />
                <path
                  className="landing-terminal-indicator landing-terminal-indicator-slow"
                  d={terminalSma50Path}
                />
                {CANDLES.map(({ id, open, close, high, low }, index) => {
                  const x = 16 + index * 32
                  const y = Math.min(open, close)
                  const height = Math.max(12, Math.abs(open - close))
                  return (
                    <g
                      key={id}
                      className={`landing-market-candle ${close <= open ? 'is-up' : 'is-down'}`}
                    >
                      <line x1={x} x2={x} y1={high} y2={low} />
                      <rect x={x - 9} y={y} width="18" height={height} rx="1" />
                    </g>
                  )
                })}
                <path className="landing-terminal-step" d={stepPath(TERMINAL_STEP)} />
              </g>
            ))}
          </g>
        </>
      ) : (
        <>
          <g className="landing-market-scroll-track">
            {SCROLL_OFFSETS.map((offset) => (
              <g key={offset} transform={`translate(${offset} 0)`}>
                <path className="landing-tv-average" d={averagePath} />
                <path className="landing-market-trace landing-market-trace-primary" d={pricePath} />
                {LINE_VOLUME.map((height, index) => {
                  const x = 8 + index * 16
                  const current = PRICE[index]!
                  const next = PRICE[index + 1]!
                  const tone = next <= current ? 'is-cyan' : 'is-champagne'
                  return (
                    <rect
                      key={index}
                      className={`landing-tv-volume ${tone}`}
                      x={x - 5}
                      y={890 - height}
                      width="10"
                      height={height}
                    />
                  )
                })}
              </g>
            ))}
          </g>
        </>
      )}
      <g className="landing-tv-crosshair">
        <line className="landing-tv-crosshair-line" x1="220" x2="220" y1="180" y2="900" />
        <line className="landing-tv-crosshair-line" x1="0" x2="304" y1="460" y2="460" />
      </g>
    </svg>
  )
}

export const MarketAtmosphere: React.FC = () => {
  return (
    <div className="landing-market-atmosphere" aria-hidden="true">
      <ChartField side="left" />
      <ChartField side="right" />
    </div>
  )
}
